import { useEffect, useRef, useState } from "react";
import {
  ActionIcon,
  Tooltip,
  Group,
  Menu,
  Divider,
  Text,
  Box,
} from "@mantine/core";
import {
  IconZoomIn,
  IconZoomOut,
  IconRefresh,
  IconRulerMeasure,
  IconCornerDownLeft,
  IconStackForward,
  IconHandStop,
  IconZoomInArea,
  IconListDetails,
  IconList,
  IconListCheck,
  IconListTree,
  IconListSearch,
  IconLayoutList,
  IconLayout,
} from "@tabler/icons-react";
import useTransientStore from "../../data/transcientStore";
import { Map } from "ol";
import { DragBox, Draw } from "ol/interaction"; // merged interaction imports
import { shiftKeyOnly } from "ol/events/condition";
import { Extent } from "ol/extent";
import { getArea, getLength } from "ol/sphere";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
// Removed incorrect GeometryType import (module does not exist in OL build). Using string literals 'Polygon' | 'LineString'.
import Polygon from "ol/geom/Polygon";
import LineString from "ol/geom/LineString";
import { useTranslation } from "../../hooks/useTranslation";

interface MapToolbarProps {
  readonly initialCenter: [number, number];
  readonly initialZoom: number;
  // selectedGeometryWkt retained for potential future zoom-to-selected reactivation
  readonly selectedGeometryWkt?: string | null;
  readonly onReset?: () => void;
  readonly onToggleBaseLayer?: () => void;
  readonly currentBaseLayer?: "osm" | "satellite";
}

// zoom-to-selected WKT parsing removed with feature deactivation.

export default function MapToolbar({
  initialCenter,
  initialZoom,
  selectedGeometryWkt,
  onReset,
  onToggleBaseLayer,
  currentBaseLayer = "osm",
}: MapToolbarProps) {
  const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({ pageId: 304, addressSystemId: addrIdFromStore || 0, autoLoad: true });
  const map: Map | null = useTransientStore((state) => state.map);
  // Prevent unused variable lint error while feature is disabled
  // selectedGeometryWkt intentionally unused while feature disabled
  const [isPanning, setIsPanning] = useState(false);
  const [isMeasureMode, setIsMeasureMode] = useState(false);
  const [measureType, setMeasureType] = useState<"distance" | "area">(
    "distance"
  );
  const [dragBoxActive, setDragBoxActive] = useState(false);
  const [measurementResult, setMeasurementResult] = useState<string>("");
  const historyRef = useRef<{ center: [number, number]; zoom: number }[]>([]);
  const measureLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const measureDrawRef = useRef<Draw | null>(null);
  const dragBoxRef = useRef<DragBox | null>(null);

  // Capture view changes into history
  useEffect(() => {
    if (!map) return;
    const view = map.getView();
    const listener = () => {
      const center = view.getCenter();
      const zoom = view.getZoom();
      if (center && typeof zoom === "number") {
        historyRef.current.push({ center: [center[0], center[1]], zoom });
      }
    };
    view.on("change", listener);
    return () => {
      view.un("change", listener as any);
    };
  }, [map]);

  // Initialize measure layer
  useEffect(() => {
    if (!map) return;
    const source = new VectorSource();
    const layer = new VectorLayer({ source, zIndex: 500 });
    map.addLayer(layer);
    measureLayerRef.current = layer;
    return () => {
      map.removeLayer(layer);
    };
  }, [map]);

  // Toggle measure mode logic
  useEffect(() => {
    if (!map) return;
    // Clean previous interaction
    if (measureDrawRef.current) {
      map.removeInteraction(measureDrawRef.current);
      measureDrawRef.current = null;
    }
    setMeasurementResult("");
    if (!isMeasureMode) return;
    const geomType = measureType === "area" ? "Polygon" : "LineString";
    const src = measureLayerRef.current
      ? measureLayerRef.current.getSource()
      : undefined;
    if (!src) return; // guard if layer missing
    const draw = new Draw({
      source: src,
      type: geomType,
    });
    draw.on("drawend", (e: any) => {
      const geom = e.feature.getGeometry();
      if (geom instanceof Polygon && measureType === "area") {
        const area = getArea(geom); // m^2
        setMeasurementResult(`${area.toFixed(2)} m²`);
      } else if (geom instanceof LineString && measureType === "distance") {
        const length = getLength(geom); // m
        setMeasurementResult(`${length.toFixed(2)} m`);
      }
    });
    map.addInteraction(draw);
    measureDrawRef.current = draw;
  }, [isMeasureMode, measureType, map]);

  // Selection zoom DragBox
  useEffect(() => {
    if (!map) return;
    if (dragBoxRef.current) {
      map.removeInteraction(dragBoxRef.current);
      dragBoxRef.current = null;
    }
    if (!dragBoxActive) return;
    const dragBox = new DragBox({ condition: shiftKeyOnly });
    dragBox.on("boxend", () => {
      const extent: Extent = dragBox.getGeometry().getExtent();
      map.getView().fit(extent, { duration: 300 });
      setDragBoxActive(false);
    });
    map.addInteraction(dragBox);
    dragBoxRef.current = dragBox;
  }, [dragBoxActive, map]);

  const zoomIn = () => {
    if (!map) return;
    const view = map.getView();
    view.animate({ zoom: (view.getZoom() || 0) + 1, duration: 200 });
  };
  const zoomOut = () => {
    if (!map) return;
    const view = map.getView();
    view.animate({ zoom: (view.getZoom() || 0) - 1, duration: 200 });
  };
  const togglePan = () => {
    setIsPanning((p) => !p); // Pan is native; toggling just indicates mode (other tools suppressed externally)
  };
  const undo = () => {
    if (!map) return;
    // Remove current state
    historyRef.current.pop();
    const prev = historyRef.current.pop(); // Previous meaningful
    if (prev) {
      map
        .getView()
        .animate({ center: prev.center, zoom: prev.zoom, duration: 250 });
    } else {
      // Fallback to initial
      map
        .getView()
        .animate({ center: initialCenter, zoom: initialZoom, duration: 250 });
    }
  };
  const reset = () => {
    if (!map) return;
    map
      .getView()
      .animate({ center: initialCenter, zoom: initialZoom, duration: 300 });
    if (measureLayerRef.current) {
      const src = measureLayerRef.current.getSource();
      if (src) src.clear();
    }
    setMeasurementResult("");
    setIsMeasureMode(false);
    setDragBoxActive(false);
    onReset?.();
  };
  // zoomToSelected feature removed at user request.

  const toggleSelectionZoom = () => setDragBoxActive((a) => !a);
  const toggleMeasure = () => setIsMeasureMode((m) => !m);
  const switchMeasureType = () =>
    setMeasureType((t) => (t === "distance" ? "area" : "distance"));
  const baseLayerLabel =
    currentBaseLayer === "osm"
      ? getTranslation("LABEL_MAP_TOOLBAR_BASE_LAYER_STREETS") || "Streets"
      : getTranslation("LABEL_MAP_TOOLBAR_BASE_LAYER_SATELLITE") || "Satellite";
  const baseLayerTooltip = `${
    getTranslation("LABEL_MAP_TOOLBAR_SWITCH_BASE_LAYER") ||
    "Switch base layer (Current:"
  } ${baseLayerLabel})`;

  return (
    <Box
      style={{
        position: "absolute",
        bottom: 32,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1000,
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(245,247,250,0.95) 60%, rgba(235,239,245,0.92) 100%)",
        backdropFilter: "blur(6px)",
        borderRadius: 16,
        padding: "10px 10px 0px 10px",
        boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        border: "1px solid rgba(180,190,205,0.55)",
        minWidth: 480,
      }}
    >
      <Group
        gap={10}
        justify="space-between"
        style={{ width: "100%", flexWrap: "wrap" }}
      >
        <Tooltip label={getTranslation("LABEL_ZOOM_IN") || "Zoom In"} zIndex={1000}>
          <ActionIcon
            variant="filled"
            color="blue"
            onClick={zoomIn}
            size="xl"
            radius="md"
            style={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
          >
            <IconZoomIn size={22} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={getTranslation("LABEL_ZOOM_OUT") || "Zoom Out"} zIndex={1000}>
          <ActionIcon
            variant="filled"
            color="blue"
            onClick={zoomOut}
            size="xl"
            radius="md"
            style={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
          >
            <IconZoomOut size={22} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={getTranslation("LABEL_SELECTION_ZOOM_SHIFTDRAG") || "Selection Zoom (Shift+Drag)"} zIndex={1000}>
          <ActionIcon
            variant={dragBoxActive ? "filled" : "light"}
            color={dragBoxActive ? "indigo" : "gray"}
            onClick={toggleSelectionZoom}
            size="xl"
            radius="md"
            style={{
              boxShadow: dragBoxActive
                ? "0 2px 8px rgba(0,0,0,0.25)"
                : undefined,
            }}
          >
            <IconZoomInArea size={22} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={getTranslation("LABEL_PAN_MODE") || "Pan Mode"} zIndex={1000}>
          <ActionIcon
            variant={isPanning ? "filled" : "light"}
            color={isPanning ? "teal" : "gray"}
            onClick={togglePan}
            size="xl"
            radius="md"
            style={{
              boxShadow: isPanning ? "0 2px 8px rgba(0,0,0,0.25)" : undefined,
            }}
          >
            <IconHandStop size={22} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={getTranslation("LABEL_UNDO_LAST_VIEW_CHANGE") || "Undo Last View Change"} zIndex={1000}>
          <ActionIcon
            variant="light"
            color="grape"
            onClick={undo}
            size="xl"
            radius="md"
          >
            <IconCornerDownLeft size={22} />
          </ActionIcon>
        </Tooltip>
        <Menu withinPortal>
          <Menu.Target>
            <Tooltip label={getTranslation("LABEL_MEASURE") || "Measure"} zIndex={1000}>
              <ActionIcon
                variant={isMeasureMode ? "filled" : "light"}
                color={isMeasureMode ? "green" : "gray"}
                onClick={toggleMeasure}
                size="xl"
                radius="md"
                style={{
                  boxShadow: isMeasureMode
                    ? "0 2px 8px rgba(0,0,0,0.25)"
                    : undefined,
                }}
              >
                <IconRulerMeasure size={22} />
              </ActionIcon>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>
              {getTranslation("LABEL_MAP_TOOLBAR_MEASUREMENT") || "Measurement"}
            </Menu.Label>
            <Menu.Item onClick={switchMeasureType}>
              {getTranslation("LABEL_MAP_TOOLBAR_SWITCH_TO") || "Switch to"} {measureType === "distance"
                ? getTranslation("LABEL_MAP_TOOLBAR_AREA") || "Area"
                : getTranslation("LABEL_MAP_TOOLBAR_DISTANCE") || "Distance"}
            </Menu.Item>
            <Divider />
            <Text size="xs" c="dimmed">
              {getTranslation("LABEL_MAP_TOOLBAR_RESULT") || "Result:"} {measurementResult || "—"}
            </Text>
          </Menu.Dropdown>
        </Menu>
        <Tooltip label={getTranslation("LABEL_REFRESH_RESET") || "Refresh / Reset"} zIndex={1000}>
          <ActionIcon
            variant="filled"
            color="orange"
            onClick={reset}
            size="xl"
            radius="md"
            style={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
          >
            <IconRefresh size={22} />
          </ActionIcon>
        </Tooltip>
        <Tooltip label={baseLayerTooltip} zIndex={1000}>
          <ActionIcon
            variant="filled"
            color={currentBaseLayer === "osm" ? "cyan" : "violet"}
            onClick={onToggleBaseLayer}
            size="xl"
            radius="md"
            style={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
          >
            <IconStackForward size={22} />
          </ActionIcon>
        </Tooltip>
        {/* Zoom to Selected Geometry disabled at user request
        <Tooltip label={getTranslation("LABEL_ZOOM_TO_SELECTED_GEOMETRY") || "Zoom to Selected Geometry"} zIndex={1000}>
          <ActionIcon
            variant="default"
            onClick={zoomToSelected}
            disabled={!selectedGeometryWkt}
            size="lg"
          >
            <IconTarget size={18} />
          </ActionIcon>
        </Tooltip>*/}
      </Group>
      <Box style={{ marginTop: 8, width: "100%" }}>
        {isMeasureMode && (
          <Text size="sm" c="dark" fw={500} style={{ textAlign: "center" }}>
            {measureType === "distance"
              ? getTranslation("LABEL_MAP_TOOLBAR_DISTANCE") || "Distance"
              : getTranslation("LABEL_MAP_TOOLBAR_AREA") || "Area"}
            :{" "}
            {measurementResult || "—"}
          </Text>
        )}
      </Box>
    </Box>
  );
}
